import {
  createStyles,
  makeStyles,
  Theme,
  Tabs,
  Tab,
  Button,
  CircularProgress,
} from "@material-ui/core";
import React, { useEffect } from "react";
import { connect, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import {
  StyledTabContainer,
  StyledTableWrapper,
} from "../postContest/_components";
import { APIState } from "../utils/api/reducer";
import {
  ContestLineup,
  ContestPlayer,
  ContestStacks,
  StackConstraint,
} from "./store/types";
import DataGrid from "../common/Table/newTable";
import {
  contestGeneratorPlayerHeaderConfig,
  contestGeneratorLineupHeaderConfig,
  contestGeneratorStackHeaderConfig,
} from "../common/Table/ContestGeneratorConfig";
import { APIConstants } from "../utils/api/constants";
import ProgressBar from "../preContest/progressBar";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { CSVLink } from "react-csv";
import {
  removeBoostedPlayer,
  setBoostedPlayer,
  setExportableLineups,
  setFightStacks,
  triggerContestGen,
} from "./store/actions";
import { getSiteForShowdown } from "../utils/showdownHandler";
import { GridApiPro } from "@mui/x-data-grid-pro/models/gridApiPro";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Stack } from "@mui/material";
import useCookie, { getCookie, setCookie } from "react-use-cookie";
import { useQueryParams } from "../utils/queryParams";
import { isStackType, showStackTabContestGen } from "../utils/stackConstants";
import { FightStackSettings } from "../common/FightStackSettingsAdjuster/FightStackSettings";
import { SitesResponse, SlateResponse, SportsResponse } from "../common/store/types";
import { getFilename } from "../common/helpers/FilenameHelper";
import { useUserId } from "../utils/authentication";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tab: {
      marginRight: '15px',
      borderRadius: "3px 3px 0px 0px",
      fontSize: '12px',
      fontWeight: 400,
      fontFamily: 'Inter',
    },
    active: {
      fontWeight: 800,
    },
    buttonWrapper: {
      position: "absolute",
      right: 0,
      display: "flex",
      alignItems: "center",
      paddingRight: "8px",
    },
    exportButton: {
      height: 40,
      width: 150,
      borderRadius: 3,
      // backgroundColor: '#0042C1',
      padding: "10px 16px",
      margin: "4px 0",
      marginLeft: 8,
      "& a": {
        color: "white !important",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      },
      "&:hover": {
        color: "white",
        // backgroundColor: '#0042C1',
      },
      "& .MuiSvgIcon-root": {
        marginLeft: 4,
        fontSize: "1rem",
      },
    },
    goToPre: {
      display: "flex",
      padding: "8px 16px",
      justifyContent: "center",
      alignItems: "center",
      gap: "4px",
      color: "white",
      textDecoration: "none",
      backgroundColor: "#0042C1",
      borderRadius: "3px",
      textTransform: "uppercase",
      fontSize: 14,
      fontWeight: 500,
      "& .MuiSvgIcon-root": {
        color: "#7CA9FF !important",
      },
      "&:hover": {
        color: "white",
        backgroundColor: "#0042C1",
        "& .MuiSvgIcon-root": {
          color: "white !important",
        },
      },
      "&.Mui-disabled": {
        pointerEvents: "none",
        color: "white !important",
        backgroundColor: "#555555 !important",
        "& .MuiSvgIcon-root": {
          color: "white !important",
        },
      },
    },
    disabled: {
      pointerEvents: "none",
      color: "white !important",
      backgroundColor: "#555555 !important",
      "& .MuiSvgIcon-root": {
        color: "white !important",
      },
    },
    regenerate: {
      display: "flex",
      justifyContent: "center",
      padding: "8px 16px",
      alignItems: "center",
      gap: "4px",
      backgroundColor: "#008F05",
      paddingRight: "8px",
      color: "white",
      textDecoration: "none",
      borderRadius: "3px",
      textTransform: "uppercase",
      fontSize: 14,
      fontWeight: 500,
      "&:hover": {
        color: "white",
        backgroundColor: "#008F05",
      },
      "&:disabled": {
        color: "white !important",
        backgroundColor: "#555555 !important",
        "& .MuiSvgIcon-root": {
          color: "white !important",
        },
      },
    },
  })
);

interface OwnProps {
  readonly loadingPercent: string;
}

interface ReduxProps {
  readonly contestPlayers: APIState<ContestPlayer[]>;
  readonly contestLineups: APIState<ContestLineup[]>;
  readonly contestStacks: APIState<ContestStacks[]>;
  readonly loading: boolean;
  readonly generatedSlate: SlateResponse;
  readonly boostedPlayers: { [key: string]: number };
  readonly stackConstraints: StackConstraint[];
  readonly poolSize: number;
  readonly runback: StackConstraint;
  readonly fightStacks: FightStackSettings[];
  readonly selectedSport: SportsResponse;
  readonly selectedSite: SitesResponse;
  readonly selectedSlate: SlateResponse;
  readonly diversity: number;
  readonly useCustomExposures: boolean;
}

interface DispatchProps {
  readonly SetExportableLineups: (lineups: string) => void;
  readonly SetBoostedPlayer: (player: string, boostedValues: number) => void;
  readonly RemoveBoostedPlayer: (player: string, boostedValues: number) => void;
  readonly SetFightStacks: (stacks: FightStackSettings[]) => void;
  readonly TriggerContestGen: (
    slateId: string,
    user: string,
    totalLineups: number,
    boostedPlayers: { [key: string]: number },
    stackConstraints: StackConstraint[],
    fightStacks: FightStackSettings[],
    runbackPercent: number,
    diversity: number,
    removedTeams: string[],
    useCustomExposure: boolean
  ) => void;
}

type Props = OwnProps & ReduxProps & DispatchProps;

const TabsWrapper = (props: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const [tabValue, setTabValue] = React.useState("players");
  const [data, setData] = React.useState("");
  const [csvHeaders, setCsvHeaders] = React.useState("");
  const [onLoadDiversity, setOnLoadDiversity] = React.useState<number | number[]>(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isExportReady, setIsExportReady] = React.useState(false);
  const queryParams = useQueryParams();
  const userId = useUserId();
  const auth = 'awe' + userId;
  const [projMultiplier, setProjMultiplier] = React.useState(0.5);
  const [ownMultiplier, setOwnMultiplier] = React.useState(0.5);
  const [processedLineups, setProcessedLineups] = React.useState<ContestLineup[]>([]);
  const removedTeams = useSelector((state: any) => state.common.removedTeams);

  const {
    contestPlayers,
    contestLineups,
    contestStacks,
    loading,
    loadingPercent,
    generatedSlate,
    boostedPlayers,
    stackConstraints,
    poolSize,
    runback,
    selectedSlate,
    diversity,
    fightStacks,
    useCustomExposures,
    SetExportableLineups,
    SetBoostedPlayer,
    RemoveBoostedPlayer,
    TriggerContestGen,
  } = props;
  const lastGeneratedBoosts = React.useRef({});
  const lastGeneratedStackConstraints = React.useRef({});
  const boostedPlayersCookieKey = `contestGenerator-boostedPlayers`;

  const handleBoost = (
    playerAndId: string,
    boostValue: number,
    removePlayer?: boolean
  ) => {
    if (removePlayer) {
      SetBoostedPlayer(playerAndId, boostValue);
    }
    if (boostValue === 0 && !removePlayer) {
      RemoveBoostedPlayer(playerAndId, boostValue);
    } else {
      SetBoostedPlayer(playerAndId, boostValue);
    }
  };

  const stackConstraintsStringCompare = () => {
    return stackConstraints.map(x => `${x.primary}-${x.secondary}-${x.percent}`).join();
  }

  const handleRegenerate = () => {
    setCookie(boostedPlayersCookieKey, JSON.stringify(boostedPlayers), {days: 2});
    setOnLoadDiversity(diversity);

    TriggerContestGen(
      generatedSlate?.slateId,
      auth,
      poolSize,
      boostedPlayers,
      generatedSlate.type?.toUpperCase() === "SHOWDOWN" ? [] : stackConstraints,
      generatedSlate.sport?.toUpperCase() !== "MMA" ? [] : fightStacks,
      runback.percent,
      diversity,
      selectedSlate?.type?.toUpperCase() !== 'SHOWDOWN' ? removedTeams : [],
      useCustomExposures
    );
    lastGeneratedBoosts.current = boostedPlayers;
    lastGeneratedStackConstraints.current = stackConstraintsStringCompare();
  };

  useEffect(function resetLineupsOnLineupLoad() {
    if(contestLineups.status === APIConstants.STARTED) {
      setProcessedLineups([]);
      setIsExportReady(false);
    }
  }, [contestLineups.status]);

  const handleChange = (event: React.ChangeEvent<{}>, newValue: any) => {
    setTabValue(newValue);
  };

  // Function to calculate overall ranks based on multipliers
  const calculateOverallRanks = (lineups: ContestLineup[], projMult: number, ownMult: number) => {
    const sorted = [...lineups].sort((a, b) => {
      const aScore = (a.projectionRank * projMult) + (a.ownershipRank * ownMult);
      const bScore = (b.projectionRank * projMult) + (b.ownershipRank * ownMult);
      return aScore - bScore; // Lower score = better rank
    });

    sorted.forEach((lineup, index) => {
      lineup.overallRank = index + 1;
    });
  };

  const getLineups = (lineups: ContestLineup[]) => {
    if (contestLineups.status === APIConstants.SUCCEEDED && contestPlayers.data) {
      // Create a dictionary of nameAndId to ContestPlayer for quick lookup
      const playerDict = contestPlayers.data.reduce((acc, player) => {
        acc[player.nameAndId] = player;
        return acc;
      }, {} as Record<string, ContestPlayer>);

      const flattenedLineups = lineups?.map((x) => {
        // Build lineup array from lineupString using the player dictionary
        const lineup: ContestPlayer[] = x.lineupString?.map(nameAndId => playerDict[nameAndId]).filter(Boolean) || [];

        const flattenedLineup: ContestLineup = {
          ...x,
          lineup,
          projectionRank: 0, // Will be calculated below
          ownershipRank: 0,  // Will be calculated below
          overallRank: 0,    // Will be calculated below
          pitcher1:
            lineup.filter((x) => x.position.includes("P")).at(0) ||
            ({} as ContestPlayer),
          pitcher2:
            lineup.filter((x) => x.position.includes("P")).at(1) ||
            ({} as ContestPlayer),
          catcher:
            lineup.filter((x) => x.position === "C").at(0) ||
            ({} as ContestPlayer),
          firstBase:
            lineup.filter((x) => x.position === "1B").at(0) ||
            ({} as ContestPlayer),
          secondBase:
            lineup.filter((x) => x.position === "2B").at(0) ||
            ({} as ContestPlayer),
          thirdBase:
            lineup.filter((x) => x.position === "3B").at(0) ||
            ({} as ContestPlayer),
          shortStop:
            lineup.filter((x) => x.position === "SS").at(0) ||
            ({} as ContestPlayer),
          outfielder1:
            lineup.filter((x) => x.position === "OF").at(0) ||
            ({} as ContestPlayer),
          outfielder2:
            lineup.filter((x) => x.position === "OF").at(1) ||
            ({} as ContestPlayer),
          outfielder3:
            lineup.filter((x) => x.position === "OF").at(2) ||
            ({} as ContestPlayer),
        };

        return flattenedLineup;
      });

      // Calculate projection ranks (highest totalProjection = rank 1) with tie handling
      const projectionSorted = [...flattenedLineups].sort((a, b) => b.totalProjection - a.totalProjection);
      projectionSorted.forEach((lineup, index) => {
        lineup.projectionRank = index + 1;
      });
      // Handle ties for projection ranks
      const projectionTies = projectionSorted.reduce((acc, lineup) => {
        if (!acc[lineup.totalProjection]) acc[lineup.totalProjection] = [];
        acc[lineup.totalProjection].push(lineup);
        return acc;
      }, {} as Record<number, ContestLineup[]>);
      Object.values(projectionTies).forEach(tiedLineups => {
        if (tiedLineups.length > 1) {
          const minRank = Math.min(...tiedLineups.map(l => l.projectionRank));
          tiedLineups.forEach(lineup => lineup.projectionRank = minRank);
        }
      });

      // Calculate ownership ranks (highest totalOwnership = rank 1) with tie handling
      const ownershipSorted = [...flattenedLineups].sort((a, b) => b.totalOwnership - a.totalOwnership);
      ownershipSorted.forEach((lineup, index) => {
        lineup.ownershipRank = index + 1;
      });
      // Handle ties for ownership ranks
      const ownershipTies = ownershipSorted.reduce((acc, lineup) => {
        if (!acc[lineup.totalOwnership]) acc[lineup.totalOwnership] = [];
        acc[lineup.totalOwnership].push(lineup);
        return acc;
      }, {} as Record<number, ContestLineup[]>);
      Object.values(ownershipTies).forEach(tiedLineups => {
        if (tiedLineups.length > 1) {
          const minRank = Math.min(...tiedLineups.map(l => l.ownershipRank));
          tiedLineups.forEach(lineup => lineup.ownershipRank = minRank);
        }
      });

      // Duplicate lineups based on dupes field
      const duplicatedLineups: ContestLineup[] = [];
      let uniqueIdCounter = 1;
      
      flattenedLineups.forEach(lineup => {
        // Add the original lineup with unique ID
        const originalLineup = { ...lineup, id: uniqueIdCounter++ };
        duplicatedLineups.push(originalLineup);
        
        // Add duplicates based on dupes field (dupes = 2 means add 2 more copies)
        for (let i = 0; i < lineup.dupes; i++) {
          duplicatedLineups.push({
            ...lineup,
            id: uniqueIdCounter++, // Generate unique ID for each duplicate
            // Create unique copies for position-specific fields to avoid reference issues
            pitcher1: { ...lineup.pitcher1 },
            pitcher2: { ...lineup.pitcher2 },
            catcher: { ...lineup.catcher },
            firstBase: { ...lineup.firstBase },
            secondBase: { ...lineup.secondBase },
            thirdBase: { ...lineup.thirdBase },
            shortStop: { ...lineup.shortStop },
            outfielder1: { ...lineup.outfielder1 },
            outfielder2: { ...lineup.outfielder2 },
            outfielder3: { ...lineup.outfielder3 },
            lineup: [...lineup.lineup] // Create new array reference
          });
        }
      });

      // Calculate overall ranks using current multipliers
      calculateOverallRanks(duplicatedLineups, projMultiplier, ownMultiplier);

      // Update processed lineups state
      setProcessedLineups(duplicatedLineups);

      return duplicatedLineups;
    }

    return [] as ContestLineup[];
  };

  const getStacks = (stacks: ContestStacks[]): ContestStacks[] => {
    if (
      !stacks ||
      (stacks && stacks.length === 0) ||
      Array.from(stacks).length === 0
    ) {
      return [];
    }
    console.log('stacks', stacks, stackConstraints);
    const stackExposures: ContestStacks[] = stacks.map((x) => {
      const desiredExposure = x.stackType === "Unstacked" ? 
        (1 - stackConstraints.map(x => x.percent).reduce((a, b) => a + b, 0)) * 100 :
        (stackConstraints.find((y) =>
          isStackType(x, y, generatedSlate.sport)
        )?.percent || 0) * 100;
      return {
        ...x,
        id: x.stackType,
        desiredExposure:
          desiredExposure > 0 ? `${desiredExposure.toFixed(0)}%` : "-",
        difference:
          desiredExposure > 0
            ? `${(x.exposure * 100 - desiredExposure).toFixed(1)}%`
            : "-",
      };
    });

    return stackExposures;
  };

  const handleProjMultiplierChange = (newPct: number) => {
    setProjMultiplier(newPct);
    setOwnMultiplier(1 - newPct);
    // Recalculate overall ranks with new multipliers
    if (processedLineups.length > 0) {
      calculateOverallRanks(processedLineups, newPct, 1 - newPct);
      setProcessedLineups([...processedLineups]); // Trigger re-render
    }
  };
  const handleOwnMultiplierChange = (newPct: number) => {
    setOwnMultiplier(newPct);
    setProjMultiplier(1 - newPct);
    // Recalculate overall ranks with new multipliers
    if (processedLineups.length > 0) {
      calculateOverallRanks(processedLineups, 1 - newPct, newPct);
      setProcessedLineups([...processedLineups]); // Trigger re-render
    }
  };

  const footer = (apiRef: React.MutableRefObject<GridApiPro>) => {
    const getSortedExport =() => {
      if (tabValue !== "lineups") {
        return data;
      }
      let csvData = csvHeaders + "\n";
      const rows = apiRef.current.getSortedRows() as ContestLineup[];
      rows.forEach((x) => {
        if (x.lineupString?.length > 0) {
          csvData = csvData.concat(x.lineupString.join(",") + "\n");
        }
      });
      return csvData;
    }

    return (
      <Stack className={classes.buttonWrapper} spacing={1} direction="row">
        <Button
          className={classes.exportButton}
          disabled={contestLineups.data?.length === 0}
          variant="text"
        >
          <CSVLink
            // data={apiRef.current.getDataAsCsv()}
            data={getSortedExport()}
            filename={getFilename(generatedSlate, '_Contest_Generator_Lineups')}
            target="_blank"
          >
            Export to CSV
          </CSVLink>
        </Button>
        <Button
          className={classes.regenerate}
          disabled={
            (boostedPlayers === lastGeneratedBoosts.current ||
            (Object.keys(boostedPlayers).length === 0 &&
              Object.keys(lastGeneratedBoosts.current).length === 0))
              && stackConstraintsStringCompare() === lastGeneratedStackConstraints.current
              && diversity === onLoadDiversity
          }
          variant="text"
          onClick={() => handleRegenerate()}
        >
          Regenerate Lineups
          <RefreshIcon />
        </Button>
        <Button
          className={classes.goToPre}
          disabled={
            !isExportReady ||
            !(
              JSON.stringify(boostedPlayers) === JSON.stringify(lastGeneratedBoosts.current) ||
              (Object.keys(boostedPlayers).length === 0 &&
                Object.keys(lastGeneratedBoosts.current).length === 0)
            )
          }
          onClick={() => {
            const url = `/precontestsimulator/${generatedSlate.sport}${queryParams?.toString() ? `?${queryParams?.toString()}` : ''}`;
            history.push(url);
          }}
        >
          Simulate Lineups
          {contestLineups.status === APIConstants.STARTED || (!isExportReady && contestLineups.data?.length > 0) ? (
            <CircularProgress size={18} style={{ color: 'white', marginLeft: 4 }} />
          ) : (
            <ArrowForwardIcon />
          )}
        </Button>
      </Stack>
    );
  };

  React.useEffect(() => {
    const savelineups = async () => {
      if (contestLineups.data?.length > 0 && generatedSlate?.sport && generatedSlate?.type) {
        let csvData = "";

        try {
          const header = await fetch(
            `${process.env.REACT_APP_API_URL}/api/contests/csvHeaders?sport=${
              generatedSlate?.sport
            }&site=${
              generatedSlate?.type?.toUpperCase() === "SHOWDOWN"
                ? getSiteForShowdown(generatedSlate?.site)
                : generatedSlate?.site
            }`
          ).then((res) => res.json());
          setCsvHeaders(header);
          csvData = header + "\n";
        } catch (error) {
          console.error('Failed to fetch CSV headers:', error);
          // Continue without headers - still allow export and simulate
          csvData = "";
        }

        contestLineups.data?.forEach((x) => {
          if (x.lineupString?.length > 0) {
            // Add the original lineup
            csvData = csvData.concat(x.lineupString.join(",") + "\n");

            // Add duplicates based on dupes field
            for (let i = 0; i < x.dupes; i++) {
              csvData = csvData.concat(x.lineupString.join(",") + "\n");
            }
          }
        });
        setData(csvData);
        SetExportableLineups(csvData);
        setIsExportReady(true);
      }
    };
    savelineups();
  }, [contestLineups, generatedSlate]);

  React.useEffect(() => {
    const boostCookie = getCookie(boostedPlayersCookieKey);
    let parsedBoosts;
    try{
      parsedBoosts = JSON.parse(boostCookie);
    } catch {
      parsedBoosts = {};
    }
    lastGeneratedBoosts.current = parsedBoosts;
  }, [contestLineups, lastGeneratedBoosts]);

  React.useEffect(() => {
    if(lastGeneratedStackConstraints.current !== stackConstraintsStringCompare()) {
      lastGeneratedStackConstraints.current = stackConstraintsStringCompare();
    }
    setOnLoadDiversity(diversity);
  }, [])

  React.useEffect(() => {
    setIsLoading(loading);
    if(loading){
      setTimeout(() => {
        setIsLoading(false);
      }, 240000);
    }
  }, [loading])

  // React.useEffect(() => {
  //   const savedTeamControl = localStorage.getItem(savedTeamControlKey);

  //   if(savedTeamControl && contestPlayers.data?.length > 0) {
  //     const players = contestPlayers.data.filter(x => savedTeamControl.includes(x.team));
  //     if(players?.length > 0) {
  //       players.forEach(player => {
  //         handleBoost(player.nameAndId + '|' + player.position, 0, true);
  //       })
  //       lastGeneratedBoosts.current = boostedPlayers;
  //     }
  //   }
  // }, [selectedSlate, selectedSite, selectedSport, contestPlayers]);

  return (
    <>
      <StyledTabContainer
        style={{
          width: "100%",
          maxHeight: "100%",
          height: "auto",
          margin: "10px 0",
        }}
      >
        <Tabs
          value={tabValue}
          indicatorColor="primary"
          textColor="inherit"
          onChange={handleChange}
        >
          <Tab
            value="players"
            label="Players"
            className={`${classes.tab} ${
              tabValue === "players" ? classes.active : ""
            }`}
          ></Tab>
          {showStackTabContestGen(selectedSlate) && (
            <Tab
              value="stacks"
              label="Stacks"
              className={`${classes.tab} ${
                tabValue === "stacks" ? classes.active : ""
              }`}
            />
          )}
          <Tab
            value="lineups"
            label="Lineups & Ranker"
            className={`${classes.tab} ${
              tabValue === "lineups" ? classes.active : ""
            }`}
          ></Tab>
        </Tabs>
        <StyledTableWrapper paddingBottom={2}>
          {isLoading && <ProgressBar percentage={loadingPercent} contestGen />}
          {!isLoading && tabValue === "players" && (
            <DataGrid
              includeStacks={false}
              lineups={contestPlayers.data}
              headers={contestGeneratorPlayerHeaderConfig(
                handleBoost,
                boostedPlayers,
                selectedSlate,
                false
              )}
              isLoading={contestPlayers.status === APIConstants.STARTED}
              selectedView="players"
              setPlayerList={() => console.log("")}
              setStackList={() => console.log("")}
              simType={"contest" + auth?.toLowerCase()}
              setStackView={(view: string) => console.log("")}
              pagination
              emptyText="Fill out settings to start generating."
              fontSize={14}
              footer={(apiRef) => footer(apiRef)}
              backgroundColor="rgba(0, 118, 54, 0.15)"
              // compact='compact'
            />
          )}
          {!loading && tabValue === "stacks" && (
            <DataGrid
              includeStacks={false}
              lineups={getStacks(contestStacks.data)}
              headers={contestGeneratorStackHeaderConfig}
              isLoading={contestStacks.status === APIConstants.STARTED}
              selectedView="stacks"
              setPlayerList={() => console.log("")}
              setStackList={() => console.log("")}
              simType={"contest" + auth?.toLowerCase()}
              setStackView={(view: string) => console.log("")}
              pagination
              emptyText="Fill out settings to start generating."
              fontSize={14}
              footer={(apiRef) => footer(apiRef)}
              // compact='compact'
              backgroundColor="rgba(0, 118, 54, 0.15)"
            />
          )}
          {!loading && tabValue === "lineups" && (
            <DataGrid
              includeStacks={false}
              lineups={processedLineups.length > 0 ? processedLineups : getLineups(contestLineups.data)}
              headers={contestGeneratorLineupHeaderConfig(
                projMultiplier,
                ownMultiplier,
                (newPct: number) => handleProjMultiplierChange(newPct),
                (newPct: number) => handleOwnMultiplierChange(newPct),
                generatedSlate,
                data
              )}
              isLoading={contestLineups.status === APIConstants.STARTED}
              selectedView="lineup"
              setPlayerList={() => console.log("")}
              setStackList={() => console.log("")}
              simType={"contest" + auth?.toLowerCase()}
              setStackView={(view: string) => console.log("")}
              pagination
              emptyText="Fill out settings to start generating."
              fontSize={14}
              footer={(apiRef) => footer(apiRef)}
              backgroundColor="rgba(0, 118, 54, 0.15)"
              // compact='compact'
            />
          )}
        </StyledTableWrapper>
      </StyledTabContainer>
    </>
  );
};
const mapStateToProps = (state: any) => {
  const {
    contestPlayers,
    contestLineups,
    contestStacks,
    loading,
    generatedSlate,
    boostedPlayers,
    stackConstraints,
    poolSize,
    runback,
    diversity,
    fightStacks,
    useCustomExposures,
  } = state.contestGenerator;

  const {
    selectedSport,
    selectedSite,
    selectedSlate
  } = state.common;
  
  return {
    contestPlayers,
    contestLineups,
    contestStacks,
    loading,
    generatedSlate,
    boostedPlayers,
    stackConstraints,
    poolSize,
    runback,
    selectedSite,
    selectedSport,
    selectedSlate,
    diversity,
    fightStacks,
    useCustomExposures
  };
};

const mapDispatchToProps = {
  SetExportableLineups: setExportableLineups,
  SetBoostedPlayer: setBoostedPlayer,
  RemoveBoostedPlayer: removeBoostedPlayer,
  TriggerContestGen: triggerContestGen,
  SetFightStacks: setFightStacks
};

export default connect(mapStateToProps, mapDispatchToProps)(TabsWrapper);
